// ─────────────────────────────────────────────────────────────────────────────
// After a deploy, a tab opened before it still runs the old build, and its
// code-split files (named by hash) are gone from the server. The first time it
// needs one, the import fails. The fix is a reload, which picks up the new
// build; done here automatically, at most once a minute so a real outage can't
// loop.
// ─────────────────────────────────────────────────────────────────────────────

const STALE = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

export const isStaleBuildError = (e: unknown) => STALE.test(String((e as { message?: string })?.message ?? e));

/** Reload for the new build. Returns false (and doesn't reload) if it already tried in the last minute. */
export function reloadForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem('stale-build-reload') ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem('stale-build-reload', String(Date.now()));
  } catch { /* no storage: reload anyway */ }
  window.location.reload();
  return true;
}

export function watchForStaleBuild() {
  window.addEventListener('vite:preloadError', e => { if (reloadForNewBuild()) e.preventDefault(); });
  window.addEventListener('unhandledrejection', e => { if (isStaleBuildError(e.reason)) reloadForNewBuild(); });
}

// ── Noticing a new build in a tab that's been open a while ──────────────────
// Moving between pages never reloads the site, so a tab left open (a student's
// all week) would keep running the old version. Every few minutes, and when the
// tab comes back into view, check which build the server has; if it's newer,
// the next page change reloads (useReloadOnNewBuild), so nobody loses a half-
// typed answer to a surprise reload.

let newer = false;
const BUILD = /assets\/index-[\w-]+\.js/;

function currentBuild(): string | null {
  const s = [...document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')].find(x => BUILD.test(x.src));
  return s ? s.src.match(BUILD)![0] : null;          // null in development
}

async function checkForNewBuild() {
  const mine = currentBuild();
  if (!mine || newer) return;
  try {
    const html = await (await fetch('/', { cache: 'no-store' })).text();
    const live = html.match(BUILD)?.[0];
    if (live && live !== mine) newer = true;
  } catch { /* offline: try again later */ }
}

export function watchForNewBuild() {
  if (!currentBuild()) return;
  window.setInterval(() => void checkForNewBuild(), 5 * 60_000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void checkForNewBuild(); });
}

/** Call with the current path: when it changes and a newer build is out, reload into it. */
export function reloadIfNewBuild() {
  if (newer) window.location.reload();
}
